import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="27451" className="flex gap-4">
        {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-4 w-16 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn("group relative text-base font-medium text-primary transition-colors hover:text-secondary", "after:absolute after:left-0 after:bottom-0 after:h-[2px] after:w-0 after:bg-secondary after:transition-all after:duration-300 hover:after:w-full")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="p-4 text-center text-sm text-muted-foreground">A carregar menu...</div>;
  }
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-primary hover:text-secondary transition-colors" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="pt-4 flex flex-col gap-3 border-t border-border/50">
        <Button asChild variant="outline" className="w-full shadow-md transition-all font-semibold">
          <Link to="/" onClick={onClose}>Home</Link>
        </Button>
        <Button asChild variant="secondary" className="w-full shadow-md transition-all font-semibold">
          <Link to="#agende-a-sua-visita">Contactar</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("fixed top-0 left-0 z-50 w-full transition-all duration-300 ease-in-out bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-md shadow-md border-b border-border/40 py-3" : "bg-background py-5 border-b border-border/10")}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>

          <div className="hidden md:block">
            <MenuProvider menu_id="16" className="w-full">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          <div className="hidden md:flex items-center gap-6">
            <Button asChild variant="outline" className="hover:shadow-lg transition-all font-semibold shadow-md px-6">
              <Link to="/">Home</Link>
            </Button>
            <Button asChild variant="secondary" className="hover:shadow-lg transition-all font-semibold shadow-md px-6">
              <Link to="#agende-a-sua-visita">Contactar</Link>
            </Button>
          </div>

          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-primary hover:bg-accent/50" aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-xl md:hidden animate-in slide-in-from-top-5 fade-in duration-200">
          <MenuProvider menu_id="16">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}